import React, { useState } from 'react';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { useHistory, Link } from 'react-router-dom';
import { Input, Button, Typography, Grid } from 'antd';
import { MailOutlined, LockOutlined } from '@ant-design/icons';
import { THEME_COLORS } from '../utils/colors';
import { useExtensionAuthFlow } from '../hooks/useExtensionAuthFlow';
import hooptieLogo from '../assets/images/HooptieNewLogo.png';
import './SignIn.css';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const CARD_BG = '#1A1A2E';

const SignIn = () => {
  const { signIn, setActive, isLoaded } = useSignIn();
  const { isSignedIn } = useAuth();
  const history = useHistory();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { handleExtensionAuthError } = useExtensionAuthFlow();

  React.useEffect(() => {
    if (isSignedIn) {
      const extensionAuth = sessionStorage.getItem('extensionAuth');
      if (!extensionAuth) {
        history.push('/dashboard');
      }
    }
  }, [isSignedIn, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;

    setLoading(true);
    setError('');

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        history.push('/dashboard');
      } else {
        console.log('Sign-in incomplete:', result);
      }
    } catch (err) {
      if (handleExtensionAuthError(err)) {
        return;
      }
      setError(err.errors?.[0]?.message || 'An error occurred during sign in');
      console.error('Sign-in error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`signin-page ${isMobile ? 'signin-page-mobile' : ''}`}
      style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: THEME_COLORS.DARK.bg.primary,
        margin: 0,
        padding: 0,
      }}
    >
      {/* Left Section - Branding (keep existing background) */}
      <div
        className="signin-left"
        style={{
          flex: isMobile ? '0 0 auto' : '0 0 50%',
          background: 'linear-gradient(135deg, #1a1d2d 0%, #141726 50%, #0d0f1a 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '8px 24px 32px' : '12px 56px 48px',
          position: 'relative',
          minHeight: isMobile ? '280px' : 'auto',
        }}
      >
        <div className="signin-left-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
          <div className="signin-brand-block">
            {isMobile ? (
              <div className="signin-mobile-logo-wrap">
                <img
                  src={hooptieLogo}
                  alt="Hooptie"
                  className="signin-mobile-logo-img"
                />
              </div>
            ) : (
              <div className="signin-logo-wrap">
                <img
                  src={hooptieLogo}
                  alt="Hooptie"
                  className="signin-logo-img"
                />
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="signin-left-footer">
            Secure sign-in • © 2026 Hooptie
          </div>
        )}
      </div>

      {/* Right Section - Sign-in form card */}
      <div
        className="signin-right"
        style={{
          flex: isMobile ? '1 1 auto' : '0 0 50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '32px 20px' : '48px',
          background: THEME_COLORS.DARK.bg.primary,
        }}
      >
        <div
          className="signin-card"
          style={{
            width: '100%',
            maxWidth: '440px',
            background: CARD_BG,
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255,255,255,0.04)',
            padding: isMobile ? '32px 24px' : '40px',
          }}
        >
          <Title level={2} className="signin-card-title">
            Sign in to Hooptie
          </Title>
          <Text className="signin-card-subtitle">
            Welcome back. Sign in to continue.
          </Text>

          <form onSubmit={handleSubmit}>
            <div className="signin-field-wrap">
              <Input
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="large"
                className="signin-input"
                prefix={isMobile ? <MailOutlined className="signin-input-icon" /> : null}
                required
              />
            </div>
            <div className="signin-field-wrap">
              <Input.Password
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                size="large"
                className="signin-input signin-input-password"
                prefix={isMobile ? <LockOutlined className="signin-input-icon" /> : null}
                required
              />
            </div>
            <div className="signin-forgot-wrap">
              <Link
                to="/sign-in"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/sign-in#forgot-password';
                }}
                className="signin-forgot-link"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="signin-error">
                {error}
              </div>
            )}

            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loading}
              disabled={!isLoaded}
              className="signin-submit-btn"
            >
              Sign In
            </Button>

            <p className="signin-signup-prompt">
              Don&apos;t have an account?{' '}
              <Link to="/onboarding?step=signup" className="signin-signup-link">
                Sign up
              </Link>
            </p>
          </form>

          <p className="signin-legal">
            By signing in, you agree to our{' '}
            <Link to="/terms-of-service" className="signin-link">Terms</Link>
            {' and '}
            {isMobile ? (
              <span className="signin-link signin-link-disabled">Privacy.</span>
            ) : (
              <Link to="/privacy-policy" className="signin-link">Privacy.</Link>
            )}
          </p>
          <p className="signin-support">
            Need help? <Link to="/support" className="signin-link">Contact support</Link>
          </p>
          {isMobile && (
            <p className="signin-mobile-copyright">© 2026 Hooptie</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
